// Derives wall/panel/area geometry from a floor's authored rooms, openings
// and voids - generalized from the algorithm Dwindling's game hand-wrote for
// its one house (buildHouse.ts), so a Studio project's map editor can use it
// interactively instead of it living as a game-specific script.
//
// The core idea: rooms tile edge to edge, so a shared edge would otherwise
// produce two coincident wall boxes (z-fighting, doubled draw calls, two
// chances to forget a doorway). Instead every room edge is projected onto
// its line, overlapping runs on the same line are merged, doorway gaps are
// subtracted from the merged runs, and what survives becomes one wall
// segment each - with a lintel filled back in above every doorway.
//
// Deliberately does not attempt Dwindling's cross-floor void "collar"/"cap"
// treatment (closing the gap between this floor's ceiling and the storey
// above's floor around a stairwell) - that needs floor-above context this
// function doesn't take, and is a known, documented simplification for this
// first pass: a void's own perimeter is walled up to `ceilingHeight`, same
// as a room, and nothing closes it above that yet.

import type {
  MapAreaMeta,
  MapFloorLevel,
  MapFloorPanel,
  MapOpeningDraft,
  MapRoomDraft,
  MapVoidDraft,
  MapWallSegment,
} from '../content/schema/map';

const EPSILON = 1e-6;
const DEFAULT_WALL_COLOR = '#5a5348';
const DEFAULT_FLOOR_COLOR = '#4a443c';
const DEFAULT_WALL_THICKNESS = 0.24;

/** A closed interval along a wall line, plus the room/void that contributed it. */
interface Contribution {
  from: number;
  to: number;
  ownerId: string;
}

function lineKey(axis: 'x' | 'z', at: number): string {
  // Coordinates are authored to 0.1 m; rounding keeps float drift from
  // splitting one shared edge into two nearly-identical lines.
  return `${axis}:${at.toFixed(3)}`;
}

function round(value: number): number {
  return Number(value.toFixed(3));
}

/** Prefix every id this module produces with, so a caller re-deriving after
 *  an edit can identify and replace exactly its own previous output without
 *  touching hand-drawn walls/panels from the editor's other tools. */
const DERIVED_PREFIX = 'room-derived-';

/** Turn a dynamic id fragment (a line key, a rounded coordinate) into one
 *  safe to embed in a `MapWallSegment.id` - single pass, so a negative
 *  coordinate's minus sign never collides with the word separators either
 *  side of it the way a two-step replace can. Only ever applied to the
 *  dynamic part; `DERIVED_PREFIX`'s own hyphens stay readable. */
function safeFragment(raw: string): string {
  return raw.replace(/[^a-zA-Z0-9_]/g, '_');
}

function mergeIntervals(contributions: Contribution[]): Array<{ from: number; to: number }> {
  const sorted = [...contributions].sort((a, b) => a.from - b.from);
  const merged: Array<{ from: number; to: number }> = [];
  for (const entry of sorted) {
    const last = merged[merged.length - 1];
    if (last && entry.from <= last.to + EPSILON) {
      last.to = Math.max(last.to, entry.to);
    } else {
      merged.push({ from: entry.from, to: entry.to });
    }
  }
  return merged;
}

/** Remove every gap from a run, returning the surviving sub-intervals. */
function subtractGaps(
  run: { from: number; to: number },
  gaps: Array<{ from: number; to: number }>,
): Array<{ from: number; to: number }> {
  let pieces = [run];
  for (const gap of gaps) {
    const next: Array<{ from: number; to: number }> = [];
    for (const piece of pieces) {
      if (gap.to <= piece.from + EPSILON || gap.from >= piece.to - EPSILON) {
        next.push(piece);
        continue;
      }
      if (gap.from > piece.from + EPSILON) next.push({ from: piece.from, to: gap.from });
      if (gap.to < piece.to - EPSILON) next.push({ from: gap.to, to: piece.to });
    }
    pieces = next;
  }
  // Sub-millimetre slivers are wall stubs nobody asked for.
  return pieces.filter((piece) => piece.to - piece.from > 0.02);
}

/**
 * Clear height of a doorway: the underside of its head. Capped at a normal
 * door's height and margined off the ceiling, the same rule of thumb
 * Dwindling's own house uses.
 */
function doorHeadHeight(ceilingHeight: number): number {
  return Math.min(2.05, ceilingHeight - 0.45);
}

export interface DeriveRoomGeometryInput {
  floorLevel: MapFloorLevel;
  rooms: MapRoomDraft[];
  openings: MapOpeningDraft[];
  voids: MapVoidDraft[];
  /** Ceiling height every derived wall/panel on this floor uses, metres. */
  ceilingHeight: number;
  /** Thickness of a generated wall slab, metres. Defaults to 0.24. */
  wallThickness?: number;
}

export interface DeriveRoomGeometryResult {
  walls: MapWallSegment[];
  panels: MapFloorPanel[];
  areas: MapAreaMeta[];
}

/**
 * Rooms, openings and voids -> the wall/panel/area primitives the runtime
 * already knows how to render and collide with. Every id this produces is
 * prefixed `room-derived-`, so a caller re-deriving after an edit can
 * identify and replace exactly its own previous output without touching
 * hand-drawn walls/panels from the editor's other tools.
 */
export function deriveRoomGeometry(input: DeriveRoomGeometryInput): DeriveRoomGeometryResult {
  const wallThickness = input.wallThickness ?? DEFAULT_WALL_THICKNESS;
  const { floorLevel, rooms, openings, voids, ceilingHeight } = input;

  const lines = new Map<string, { axis: 'x' | 'z'; at: number; contributions: Contribution[] }>();
  const contribute = (axis: 'x' | 'z', at: number, from: number, to: number, ownerId: string) => {
    const key = lineKey(axis, at);
    let line = lines.get(key);
    if (!line) {
      line = { axis, at, contributions: [] };
      lines.set(key, line);
    }
    line.contributions.push({ from, to, ownerId });
  };

  for (const room of rooms) {
    // Vertical lines (constant X, running along Z).
    contribute('x', room.x[0], room.z[0], room.z[1], room.id);
    contribute('x', room.x[1], room.z[0], room.z[1], room.id);
    // Horizontal lines (constant Z, running along X).
    contribute('z', room.z[0], room.x[0], room.x[1], room.id);
    contribute('z', room.z[1], room.x[0], room.x[1], room.id);
  }
  // A void is walled exactly like a room - otherwise the shaft would have no
  // sides - it just contributes no panel or area below (see the map at the
  // bottom of this function).
  for (const hole of voids) {
    contribute('x', hole.x[0], hole.z[0], hole.z[1], hole.finishFrom);
    contribute('x', hole.x[1], hole.z[0], hole.z[1], hole.finishFrom);
    contribute('z', hole.z[0], hole.x[0], hole.x[1], hole.finishFrom);
    contribute('z', hole.z[1], hole.x[0], hole.x[1], hole.finishFrom);
  }

  const gapsByLine = new Map<string, Array<{ from: number; to: number }>>();
  for (const opening of openings) {
    const key = lineKey(opening.axis, opening.at);
    const list = gapsByLine.get(key) ?? [];
    list.push({ from: opening.center - opening.width / 2, to: opening.center + opening.width / 2 });
    gapsByLine.set(key, list);
  }

  const roomsById = new Map(rooms.map((room) => [room.id, room] as const));
  const walls: MapWallSegment[] = [];

  /** The room whose finish a stretch of this line should take. */
  const ownerOf = (line: { contributions: Contribution[] }, from: number, to: number) => {
    const owner = line.contributions.find((c) => c.from < to - EPSILON && c.to > from + EPSILON);
    return owner ? roomsById.get(owner.ownerId) : undefined;
  };

  for (const [key, line] of lines) {
    const gaps = gapsByLine.get(key) ?? [];
    for (const run of mergeIntervals(line.contributions)) {
      for (const span of subtractGaps(run, gaps)) {
        const length = span.to - span.from;
        if (length <= 0.02) continue;
        const room = ownerOf(line, span.from, span.to);
        const mid = (span.from + span.to) / 2;
        const centreY = ceilingHeight / 2;

        walls.push({
          id: DERIVED_PREFIX + 'wall-' + safeFragment(`${key}-${round(mid)}`),
          position:
            line.axis === 'x'
              ? [round(line.at), round(centreY), round(mid)]
              : [round(mid), round(centreY), round(line.at)],
          size:
            line.axis === 'x'
              ? [wallThickness, round(ceilingHeight), round(length)]
              : [round(length), round(ceilingHeight), wallThickness],
          color: room?.wallSurface?.color ?? DEFAULT_WALL_COLOR,
          surface: room?.wallSurface,
        });
      }
    }
  }

  // ── Lintels ──────────────────────────────────────────────────────────────
  // A doorway is a gap in a wall, not a hole in a building: subtracting the
  // opening removed the wall over its full height, leaving an open strip
  // from the top of the door frame to the ceiling that looked straight
  // through into the next room. Fill each opening from its head up to the
  // ceiling.
  for (const opening of openings) {
    const key = lineKey(opening.axis, opening.at);
    const line = lines.get(key);
    if (!line) continue;
    const head = doorHeadHeight(ceilingHeight);
    const lintelHeight = ceilingHeight - head;
    if (lintelHeight <= 0.02) continue;

    const from = opening.center - opening.width / 2;
    const to = opening.center + opening.width / 2;
    const room = ownerOf(line, from, to);
    // Run a touch wider than the gap so the lintel's ends bury themselves in
    // the stubs either side rather than meeting them face to face.
    const length = opening.width + wallThickness / 6;
    const centreY = head + lintelHeight / 2;

    walls.push({
      id: DERIVED_PREFIX + 'lintel-' + safeFragment(`${key}-${round(opening.center)}`),
      position:
        opening.axis === 'x'
          ? [round(opening.at), round(centreY), round(opening.center)]
          : [round(opening.center), round(centreY), round(opening.at)],
      size:
        opening.axis === 'x'
          ? [wallThickness, round(lintelHeight), round(length)]
          : [round(length), round(lintelHeight), wallThickness],
      color: room?.wallSurface?.color ?? DEFAULT_WALL_COLOR,
      surface: room?.wallSurface,
    });
  }

  // Voids contribute walls above but no panel and no area: a stairwell is a
  // hole, not a room, and the player must not be able to stand in one.
  const panels: MapFloorPanel[] = rooms.map((room) => ({
    id: room.id,
    center: [round((room.x[0] + room.x[1]) / 2), round((room.z[0] + room.z[1]) / 2)],
    size: [round(room.x[1] - room.x[0]), round(room.z[1] - room.z[0])],
    color: room.floorSurface?.color ?? DEFAULT_FLOOR_COLOR,
    ceilingHeight,
    surface: room.floorSurface,
    // `openAbove` rooms are the ones a flight rises through: capping them
    // would put a lid across the stairwell and the stairs would climb into it.
    ceiling: room.openAbove ? undefined : room.ceilingSurface,
  }));

  const areas: MapAreaMeta[] = rooms.map((room) => ({
    id: room.id,
    name: room.name,
    floor: floorLevel,
    kind: room.kind ?? 'room',
  }));

  return { walls, panels, areas };
}

/** True for any id `deriveRoomGeometry` produced - lets a caller identify
 *  and replace exactly its own previous output on re-derivation without
 *  touching hand-drawn walls/panels from the editor's other tools. */
export function isRoomDerived(id: string): boolean {
  return id.startsWith(DERIVED_PREFIX);
}
